import React from 'react';
import { get, split, isEmpty, forEach } from 'lodash';
import { getWebEnv } from '../../../api/api-env';
import Payments from '../../../api/api-payments';
import { Profile } from '../../../api/api-profile';
import { DirectDepositForm } from '../elements-forms/managed-account-direct-deposit';
import { VerifyBankAccount } from '../elements-forms/verify-bank-account';
import BillingAddressForm from './forms/BillingAddressForm';
import { StripeIntro } from './stripe-intro';
import { FormattedMessage } from 'react-intl';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import AddressFormatter from '@shopify/address';
import PropTypes from 'prop-types';
import AddTaxDetailsContainer from './taxes/AddTaxDetailsContainer';
import { Spinner } from '../shared-components/decorators/spinner';
import { StatsigContext } from '../elements-statsig-provider/statsig-context';
import { TaxDetailsFormSection } from './TaxDetailsFormSection';

export class ManagedAccount extends React.Component {
  static contextType = StatsigContext;

  constructor (props) {
    super(props);

    this.state = {
      allCountries: [],
      globalCountries: [],
      isCountriesLoading: false,
      isBankAuthLoading: 'bank' === this.props.flow || 'host' === this.props.flow,
      bankAccountUpdateSession: null
    };

    this.updateAccount = this.updateAccount.bind(this);
    this.isUpdate = this.isUpdate.bind(this);
    this.pageErrorBlock = this.pageErrorBlock.bind(this);
  }

  componentWillMount () {
    const { token, profile } = this.props;
    const ssoId = get(profile, 'SSOid', '');
    Payments.getStripePublicKeyType(ssoId, token, (resp) => {
      const keyType = get(resp, 'stripe_key_field') || 'stripe_public_key';
      const locale = SharedIntlProviderHOC.getLocale();
      getWebEnv(locale, (env) => {
        Stripe.setPublishableKey(env.stripe[keyType]);
      });
    });
  }

  componentDidMount () {
    if ('bank' === this.props.flow || 'host' === this.props.flow) {
      Profile.read('/v1/profiles/me/authorizations/bank-update', this.props.token, (response) => {
        this.setState({ bankAccountUpdateSession: response?.bankAccountUpdateSession, isBankAuthLoading: false });
      });
    }
    const locale = split(SharedIntlProviderHOC.getLocale(), '-', 1)[0];
    const localAddressFormatter = new AddressFormatter(locale);
    const globalAddressFormatter = new AddressFormatter('en');
    this.setState({ isCountriesLoading: true }, () => {
      Promise.all([
        localAddressFormatter.getCountries(),
        globalAddressFormatter.getCountries()
      ]).then((values)=> {
        this.setState({
          allCountries: values[0],
          globalCountries: values[1],
          isCountriesLoading: false
        });
      });
    });
  }

  isUpdate () {
    const { profile } = this.props;
    const customerID = profile.account_id;

    return !!customerID;
  }

  updateAccount (response) {
    this.props.updateAccount(response);
  }

  pageErrorBlock () {
    return (
      <div className="flex column middle no-content-page">
        <img style={{ width: 120 }} src="/web-hosting/assets/images/ps_logo_dark-v2.svg" />
        <h3 className="padding-xl-bottom">
          <FormattedMessage
            id="hosting.managed_account.error_page_message"
            defaultMessage="Page not found"
          />
        </h3>
        <a href="/account/methods" className="btn btn-primary btn-lg hero text-center margin-xl-top">
          <FormattedMessage
            id="hosting.managed_account.error_page_back_link_text"
            defaultMessage="Back to Payments"
          />
        </a>
      </div>);
  }

  render () {
    const { flow, hasAcct, hasBank, account, token, isVatEnabled, subFlow } = this.props;
    const eventuallyDue = get(account, 'requirements.eventually_due', []);
    const accountInProgress = 1 < eventuallyDue.length && !hasBank;

    if (this.state.isCountriesLoading || this.state.isBankAuthLoading) {
      return (
        <div className="spinner-container">
          <Spinner additionalStyle="gray" />
        </div>
      );
    }

    if ('host' === flow) {
      if (!hasAcct || accountInProgress) {
        return <StripeIntro {...this.props} inProgress={accountInProgress} linkType='account_onboarding' newHost />;
      }

      if (!hasBank) {
        if ('ACTIVE' === this.state.bankAccountUpdateSession?.status) {
          return <DirectDepositForm {...this.props} callback={this.updateAccount} hostFlow verifyFlow={false} />;
        }
        return <VerifyBankAccount accessToken={token} bankAccountUpdateSession={this.state.bankAccountUpdateSession} userEmail={this.props.profile?.email} />;
      }
      window.location.href = '/account/methods';
      return null;
    }

    if ('verify' === flow) {
      const missing = get(account, 'combined_requirements') || [];

      if (isEmpty(missing)) {
        const status = get(account, 'individual.verification.status', {});
        if ('unverified' === status) {
          return <StripeIntro {...this.props} linkType='account_onboarding' inProgress={accountInProgress} />;
        }
        window.location.href = '/account/methods';
        return null;
      }

      let acctDetails = false;
      let bank = false;
      let fullSSN = false;
      let verificationDoc = false;
      forEach(missing, (item) => {
        if ('individual.id_number' === item) {
          fullSSN = true;
        } else if ('individual.verification.document' === item) {
          verificationDoc = true;
        } else if ('external_account' === item) {
          bank = true;
        } else {
          acctDetails = true;
        }
      });

      if (bank && !fullSSN && !verificationDoc && !acctDetails) {
        return <DirectDepositForm {...this.props} callback={this.updateAccount} verifyFlow={false} />;
      }

      return <StripeIntro {...this.props} linkType='account_onboarding' inProgress={accountInProgress} />;
    }

    if ('bank' === flow) {
      if ('ACTIVE' === this.state.bankAccountUpdateSession?.status) {
        return <DirectDepositForm {...this.props} callback={this.updateAccount} verifyFlow={false} />;
      }

      return <VerifyBankAccount accessToken={token} bankAccountUpdateSession={this.state.bankAccountUpdateSession} userEmail={this.props.profile?.email} />;
    }

    if ('billing' === flow) {
      return (
        <div className="ps-container-lg-margin ps-container-fixed">
          <div className="ps-row">
            <div className="col-xs-12">
              <div className="margin-xl-top">
                <BillingAddressForm
                  accessToken={token}
                  allCountries={this.state.allCountries}
                  globalCountries={this.state.globalCountries}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if ('tax-details' === flow) {
      // Gates the rebuilt tax details form; falls back to the legacy one until
      // Statsig resolves, so nothing flashes the new UI and swaps back.
      const isVatValidationEnabled =
        this.context?.featureGates?.vat_validation ?? false;

      if (isVatValidationEnabled) {
        return (
          <div className="ps-container-lg-margin ps-container-fixed">
            <div className="ps-row">
              <div className="col-xs-12 col-md-8">
                <TaxDetailsFormSection
                  accessToken={token}
                  allCountries={this.state.allCountries}
                  globalCountries={this.state.globalCountries}
                  pageErrorBlock={this.pageErrorBlock}
                  taxDetailsScope={this.props.taxDetailsScope}
                />
              </div>
            </div>
          </div>
        );
      }

      return (
        <AddTaxDetailsContainer
          {...this.props}
          isVatEnabled={isVatEnabled}
          accessToken={token}
          pageErrorBlock={this.pageErrorBlock}
          allCountries={this.state.allCountries}
          globalCountries={this.state.globalCountries}
        />
      )
    }

    return this.pageErrorBlock();
  }
}

ManagedAccount.propTypes = {
  profile: PropTypes.object,
  updateAccount: PropTypes.func,
  flow: PropTypes.string,
  subFlow: PropTypes.string,
  token: PropTypes.string,
  hasAcct: PropTypes.bool,
  hasBank: PropTypes.bool,
  fee_profiles: PropTypes.object,
  account: PropTypes.object,
  isVatEnabled: PropTypes.bool,
  taxDetailsScope: PropTypes.string
};
